import { FormHelperText } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { PickersTextField } from "@mui/x-date-pickers/PickersTextField";
import { Dayjs } from "dayjs";
import React from "react";
import { Control, Controller, Path } from "react-hook-form";
import { COMPONENT_IDS } from "../../helpers/constants/componentIds";
import {
  getHelperText as displayHelperText,
  removeWhiteSpace as removeEmptySpace,
} from "../../helpers/utils";
import { MuiBox } from "../box/MuiBox";
import { MuiDateFieldWrapper } from "../dateField/MuiDateField.style";
import { ErrorCrossIcon } from "../errorCrossIcon/ErrorCrossIcon";
import { MuiTypography } from "../typography/MuiTypography";
import { MuiDateTimeFieldProps, OpenPickerIcon } from "./MuiDateTimeField";

interface MuiDateTimeFieldFormControllerProps extends MuiDateTimeFieldProps {
  control: Control<any>;
  name: Path<any>;
  format?: string;
  id?: string;
  disableManualInput?: boolean;
  requiredARIALabel?: string;
  selectDateTimeARIALabel?: string;
}

export const MuiDateTimeFieldFormController: React.FC<
  MuiDateTimeFieldFormControllerProps
> = ({
  onChange,
  control,
  name,
  format,
  id = COMPONENT_IDS.COMMON_DATE_TIME_PICKER_FORM_CONTROLLER,
  label,
  isMandatory,
  disableLabel,
  isErrorIconDateTimeEnabled = true,
  valueFormat,
  disableManualInput = false,
  clearable = false,
  requiredARIALabel = "Required",
  selectDateTimeARIALabel = "Select date and time",
  ...rest
}) => {
  return (
    <MuiDateFieldWrapper>
      {label && (
        <MuiBox className="title">
          <MuiTypography
            className={disableLabel ? "mui-label disabled" : "mui-label"}
            component="label"
            variant="h3"
          >
            {label}{" "}
            {isMandatory && (
              <label className="asterisk" aria-label={requiredARIALabel}>
                *
              </label>
            )}
          </MuiTypography>
        </MuiBox>
      )}
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Controller
          control={control}
          name={name}
          render={({ field, fieldState: { error, invalid } }) => (
            <>
              <DateTimePicker
                closeOnSelect={false}
                format={format}
                enableAccessibleFieldDOMStructure
                slots={{
                  textField: PickersTextField,
                  openPickerIcon: OpenPickerIcon,
                }}
                slotProps={{
                  textField: {
                    id: id,
                    inputProps: {
                      "data-testid": id,
                      "aria-label": `${label} ${
                        isMandatory && !disableLabel ? requiredARIALabel : ""
                      }`,
                    },
                    error: invalid,
                    onBlur: field.onBlur,
                  },
                  field: {
                    readOnly: disableManualInput,
                    clearable,
                  },
                  ...rest.slotProps,
                }}
                {...rest}
                onChange={(selectedDateTime: Dayjs | null) => {
                  onChange?.(selectedDateTime);
                  if (valueFormat && selectedDateTime) {
                    field.onChange(selectedDateTime.format(valueFormat));
                  } else {
                    field.onChange(selectedDateTime);
                  }
                }}
              />
              {error && (
                <FormHelperText error={invalid}>
                  {error?.message && (
                    <MuiBox
                      role="alert"
                      aria-description={displayHelperText(
                        error?.message,
                        label as string,
                      )}
                      className="error-icon"
                    >
                      {isErrorIconDateTimeEnabled && <ErrorCrossIcon />}
                      <div
                        aria-hidden
                        id={`helper-text-${removeEmptySpace(label)}`}
                      >
                        {displayHelperText(error?.message, label as string)}
                      </div>
                    </MuiBox>
                  )}
                </FormHelperText>
              )}
            </>
          )}
        />
      </LocalizationProvider>
    </MuiDateFieldWrapper>
  );
};
